import {
  TLocationAudit,
  TLocationAuditItem,
  CreateLocationAuditDto,
  UpdateLocationAuditDto,
  CompleteLocationAuditDto,
  AddAuditItemDto,
  BulkAddAuditItemsDto,
  UpdateAuditItemDto,
  LocationAuditStatus,
} from "~/types/location-audit";
import { TInventory } from "~/types/inventory";
import { baseService } from "../base";
import { PaginationResponse } from "../type";

export type GetLocationAuditsParams = {
  page?: number;
  limit?: number;
  locationId?: string;
  userId?: string;
  status?: LocationAuditStatus;
};

export type GetInventoriesByLocationParams = {
  page?: number;
  limit?: number;
};

export const locationAuditService = {
  // Create a new audit
  createAudit: async (dto: CreateLocationAuditDto) => {
    const { data } = await baseService.post<TLocationAudit>(
      "/location-audits",
      dto
    );
    return data;
  },

  // Get all audits with pagination
  getAudits: async (params?: GetLocationAuditsParams) => {
    const { data } = await baseService.get<PaginationResponse<TLocationAudit>>(
      "/location-audits",
      { params }
    );
    return data;
  },

  // Get a specific audit
  getAudit: async (id: string) => {
    const { data } = await baseService.get<TLocationAudit>(
      `/location-audits/${id}`
    );
    return data;
  },

  // Update an audit
  updateAudit: async (id: string, dto: UpdateLocationAuditDto) => {
    const { data } = await baseService.patch<TLocationAudit>(
      `/location-audits/${id}`,
      dto
    );
    return data;
  },

  // Complete an audit
  completeAudit: async (id: string, dto?: CompleteLocationAuditDto) => {
    const { data } = await baseService.post<TLocationAudit>(
      `/location-audits/${id}/complete`,
      dto || {}
    );
    return data;
  },

  // Add an item to an audit
  addItem: async (auditId: string, dto: AddAuditItemDto) => {
    const { data} = await baseService.post<TLocationAuditItem>(
      `/location-audits/${auditId}/items`,
      dto
    );
    return data;
  },

  // Bulk add items to an audit
  bulkAddItems: async (auditId: string, dto: BulkAddAuditItemsDto) => {
    const { data } = await baseService.post<TLocationAuditItem[]>(
      `/location-audits/${auditId}/items/bulk`,
      dto
    );
    return data;
  },

  // Update an audit item
  updateItem: async (auditId: string, itemId: string, dto: UpdateAuditItemDto) => {
    const { data } = await baseService.patch<TLocationAuditItem>(
      `/location-audits/${auditId}/items/${itemId}`,
      dto
    );
    return data;
  },

  // Remove an item from an audit
  removeItem: async (auditId: string, itemId: string) => {
    await baseService.delete(`/location-audits/${auditId}/items/${itemId}`);
  },

  // Get inventories by location
  getInventoriesByLocation: async (
    locationId: string,
    params?: GetInventoriesByLocationParams
  ) => {
    const { data } = await baseService.get<PaginationResponse<TInventory>>(
      `/location-audits/inventories/${locationId}`,
      { params }
    );
    return data;
  },
};
